import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { MessageSquare, Send, ShieldCheck, Loader2, Bell, AlertCircle, FileText, RotateCcw, Eye, KeyRound, Factory, Lock, Plus, X, Clock } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { useToast } from '../hooks/use-toast';

// Aug 2026 — Category-keyed plant mailing lists. Rendered under the
// Notifications tab. Super-admin only: `/admin/settings` strips the
// addresses on GET for non-supers and preserves the stored value on
// PUT. This card lazy-fetches `/admin/me` to decide whether to show
// the editors or a locked banner.
//
// Feb 2026 — Custom category slugs are now supported. Super admins
// can add ANY slug (e.g. `mc-ex`, `glace-rm-pm`, `frozen`) via the
// "Add Category" form; each new slug becomes a first-class list
// alongside the four built-ins.
const CATEGORY_META = [
  { key: 'ice-cream', label: 'Ice-cream',  brand: 'Glace',         accent: 'text-pink-700', chip: 'bg-pink-50 border-pink-200' },
  { key: 'chips',     label: 'Chips',      brand: 'Muscat Chips',  accent: 'text-amber-800', chip: 'bg-amber-50 border-amber-200' },
  { key: 'wafers',    label: 'Wafers',     brand: 'Kingsy Wafers', accent: 'text-orange-700', chip: 'bg-orange-50 border-orange-200' },
  { key: 'water',     label: 'Water',      brand: 'Saroj Water',   accent: 'text-sky-700', chip: 'bg-sky-50 border-sky-200' },
];
const BUILTIN_KEYS = new Set(CATEGORY_META.map((c) => c.key));

// Fallback map surfaced in the UI so ops know which parent category
// takes over when a custom slug has no direct recipients. MUST stay
// in sync with `_PLANT_CATEGORY_FALLBACKS` on the backend.
const CATEGORY_FALLBACK_HINT = {
  'mc-ex':       'chips',
  'glace-rm-pm': 'ice-cream',
  'frozen':      'ice-cream',
};

// Rotating accent for custom (non-builtin) categories so they still
// read distinct at a glance without the ops team having to hard-code
// palettes for every new slug they add.
const CUSTOM_ACCENT_PALETTE = [
  { accent: 'text-indigo-700', chip: 'bg-indigo-50 border-indigo-200' },
  { accent: 'text-emerald-700', chip: 'bg-emerald-50 border-emerald-200' },
  { accent: 'text-rose-700',   chip: 'bg-rose-50 border-rose-200' },
  { accent: 'text-cyan-700',   chip: 'bg-cyan-50 border-cyan-200' },
  { accent: 'text-fuchsia-700', chip: 'bg-fuchsia-50 border-fuchsia-200' },
];
const _prettify = (slug) => String(slug || '')
  .split(/[-_]/)
  .filter(Boolean)
  .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
  .join(' ');
const _normalizeSlug = (raw) => String(raw || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

const _isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || '').trim());

const PlantMailingListsCard = ({ form, update, authHeaders, apiBase }) => {
  const { toast } = useToast();
  const [isSuper, setIsSuper] = useState(null); // null=loading

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await axios.get(`${apiBase}/admin/me`, { headers: authHeaders });
        if (!cancel) setIsSuper(!!data?.is_super_admin);
      } catch { if (!cancel) setIsSuper(false); }
    })();
    return () => { cancel = true; };
  }, [apiBase, authHeaders]);

  const lists = form.plant_team_mailing_lists || {};

  // Feb 2026 — Merge built-ins with any custom slugs currently in
  // the settings doc. Custom slugs are surfaced in the same grid,
  // in insertion order, with a rotating accent palette and a small
  // "Remove list" affordance.
  const customEntries = Object.entries(lists)
    .filter(([k]) => !BUILTIN_KEYS.has(k))
    .map(([k], idx) => {
      const palette = CUSTOM_ACCENT_PALETTE[idx % CUSTOM_ACCENT_PALETTE.length];
      return {
        key: k,
        label: _prettify(k),
        brand: 'Custom category',
        accent: palette.accent,
        chip: palette.chip,
        custom: true,
      };
    });
  const allEntries = [...CATEGORY_META, ...customEntries];

  const [addSlug, setAddSlug] = useState('');
  const [addDraft, setAddDraft] = useState('');

  const addCategory = () => {
    const slug = _normalizeSlug(addSlug);
    if (!slug) {
      toast({ title: 'Category slug is required', description: 'Use lowercase letters, digits and dashes (e.g. mc-ex).' });
      return;
    }
    if (Object.prototype.hasOwnProperty.call(lists, slug) || BUILTIN_KEYS.has(slug)) {
      toast({ title: 'Category already exists', description: `"${slug}" is already in the list.` });
      return;
    }
    // Split the optional email seed on comma/semicolon/space/newline
    // so ops can create + populate in a single click.
    const parts = String(addDraft || '')
      .split(/[,;\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    const seed = parts.filter(_isEmail);
    const rejected = parts.filter((s) => !_isEmail(s));
    if (rejected.length) {
      toast({
        title: `Skipped ${rejected.length} invalid address${rejected.length === 1 ? '' : 'es'}`,
        description: rejected.slice(0, 3).join(', ') + (rejected.length > 3 ? '…' : ''),
      });
    }
    update({
      plant_team_mailing_lists: { ...lists, [slug]: seed },
    });
    setAddSlug('');
    setAddDraft('');
    toast({
      title: 'Category added',
      description: seed.length
        ? `${_prettify(slug)} — ${seed.length} recipient${seed.length === 1 ? '' : 's'} seeded. Save to persist.`
        : `${_prettify(slug)} — add recipients then Save to persist.`,
    });
  };

  const removeCategory = (slug) => {
    if (BUILTIN_KEYS.has(slug)) return;
    if (!window.confirm(`Remove the "${_prettify(slug)}" mailing list entirely? This cannot be undone from the UI.`)) return;
    const next = { ...lists };
    delete next[slug];
    update({ plant_team_mailing_lists: next });
  };

  const setListFor = (catKey, next) => {
    // De-duplicate + drop empties + preserve entry order so ops sees
    // the same layout after a save.
    const clean = [];
    const seen = new Set();
    (next || []).forEach((raw) => {
      const s = String(raw || '').trim();
      if (!s || seen.has(s.toLowerCase())) return;
      seen.add(s.toLowerCase());
      clean.push(s);
    });
    update({
      plant_team_mailing_lists: { ...lists, [catKey]: clean },
    });
  };

  if (isSuper === null) {
    return (
      <div className="bg-white rounded-2xl border border-neutral-200 p-6 lg:p-8 flex items-center gap-3 text-sm text-neutral-500" data-testid="plant-mailing-lists-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading plant mailing lists…
      </div>
    );
  }
  if (!isSuper) {
    return (
      <div className="bg-white rounded-2xl border border-neutral-200 p-6 lg:p-8" data-testid="plant-mailing-lists-locked">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-neutral-100 text-neutral-500 flex items-center justify-center">
            <Lock className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-[#0F172A]">Plant mailing lists</h2>
            <p className="text-sm text-slate-500">
              Restricted. Only super admins can view or edit which addresses receive the
              &ldquo;Notify Plant&rdquo; escalation for each category.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-neutral-200 p-6 lg:p-8" data-testid="plant-mailing-lists">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center">
          <Factory className="w-5 h-5" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-[#0F172A]">Plant mailing lists</h2>
          <p className="text-sm text-slate-500">
            One list per category. When ops fires <b>Notify Plant</b> on a shortfall item, the
            item&apos;s category picks the list and every address on it receives a copy.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="plant-mailing-lists-grid">
        {allEntries.map((cat) => (
          <PlantMailingListEditor
            key={cat.key}
            meta={cat}
            addresses={Array.isArray(lists[cat.key]) ? lists[cat.key] : []}
            fallbackTo={CATEGORY_FALLBACK_HINT[cat.key]}
            onChange={(next) => setListFor(cat.key, next)}
            onRemove={cat.custom ? () => removeCategory(cat.key) : null}
            toast={toast}
          />
        ))}
      </div>
      {/* Feb 2026 — Add-Category form. Any lowercase slug (e.g.
          `mc-ex`, `glace-rm-pm`, `frozen`) becomes a first-class
          list once created. Save from the parent Notifications
          panel to persist. */}
      <div
        className="mt-4 rounded-xl border border-dashed border-neutral-300 bg-neutral-50 p-3"
        data-testid="plant-mailing-lists-add-category"
      >
        <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-2 flex items-center gap-1.5">
          <Plus className="w-3.5 h-3.5" /> Add category
        </div>
        <div className="flex flex-col sm:flex-row gap-1.5">
          <Input
            value={addSlug}
            onChange={(e) => setAddSlug(e.target.value)}
            placeholder="Category slug (e.g. mc-ex)"
            className="h-8 text-xs sm:w-52"
            data-testid="plant-mailing-lists-add-slug"
          />
          <Input
            value={addDraft}
            onChange={(e) => setAddDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addCategory();
              }
            }}
            placeholder="Optional: seed recipient(s) — comma-separated"
            className="h-8 text-xs flex-1"
            data-testid="plant-mailing-lists-add-emails"
          />
          <Button
            type="button"
            onClick={addCategory}
            disabled={!_normalizeSlug(addSlug)}
            className="h-8 bg-[#0078D4] hover:bg-[#106EBE] text-white text-xs px-3"
            data-testid="plant-mailing-lists-add-submit"
          >
            <Plus className="w-3 h-3 mr-1" /> Add
          </Button>
        </div>
        <div className="text-[10px] text-neutral-500 mt-1.5 leading-relaxed">
          Slugs must match a product&apos;s <code className="bg-white border border-neutral-200 rounded px-1">category_slug</code>.
          A shortfall in <code className="bg-white border border-neutral-200 rounded px-1">mc-ex</code> or
          <code className="bg-white border border-neutral-200 rounded px-1 ml-1">glace-rm-pm</code> falls back to the parent retail list
          (chips / ice-cream) when this list is empty.
        </div>
      </div>
      <p className="text-[11px] text-neutral-500 mt-4">
        Paste multiple addresses at once (comma, semicolon, space, or newline). Invalid entries are highlighted before you save.
      </p>
    </div>
  );
};

const PlantMailingListEditor = ({ meta, addresses, onChange, toast, fallbackTo, onRemove }) => {
  const [draft, setDraft] = useState('');

  const addFromDraft = () => {
    const raw = draft.trim();
    if (!raw) return;
    // Split on comma/semicolon/space/newline to support paste-and-go.
    const parts = raw.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    const invalid = parts.filter((s) => !_isEmail(s));
    const good = parts.filter(_isEmail);
    if (invalid.length && toast) {
      toast({
        title: `Skipped ${invalid.length} invalid address${invalid.length === 1 ? '' : 'es'}`,
        description: invalid.slice(0, 3).join(', ') + (invalid.length > 3 ? '…' : ''),
      });
    }
    if (good.length) onChange([...(addresses || []), ...good]);
    setDraft('');
  };

  const remove = (addr) => {
    onChange((addresses || []).filter((a) => a !== addr));
  };

  return (
    <div className={`rounded-xl border ${meta.chip} p-3`} data-testid={`plant-list-${meta.key}`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className={`text-sm font-bold ${meta.accent} flex items-center gap-1.5`}>
            {meta.label}
            {meta.custom && (
              <span className="text-[9px] font-bold text-neutral-500 bg-white border border-neutral-300 rounded-full px-1.5 py-0.5 uppercase tracking-wide">
                Custom
              </span>
            )}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold flex items-center gap-1">
            <span>{meta.brand}</span>
            <span className="font-mono text-neutral-400 lowercase">· {meta.key}</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] text-neutral-600 bg-white/80 px-2 py-0.5 rounded-full">
            {addresses?.length || 0} address{(addresses?.length || 0) === 1 ? '' : 'es'}
          </span>
          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              className="p-1 rounded-full text-neutral-400 hover:text-red-700 hover:bg-red-50"
              aria-label={`Remove ${meta.label} category`}
              title={`Remove ${meta.label} category`}
              data-testid={`plant-list-remove-category-${meta.key}`}
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
      {fallbackTo && (
        <div
          className="mb-2 text-[10px] text-neutral-500 italic"
          data-testid={`plant-list-fallback-${meta.key}`}
        >
          If empty, shortfall notifications fall back to the <b>{_prettify(fallbackTo)}</b> list.
        </div>
      )}
      <div className="flex flex-wrap gap-1.5 mb-2 min-h-[28px]">
        {(addresses || []).length === 0 && (
          <span className="text-[11px] text-neutral-400 italic">No recipients yet — {fallbackTo ? `Notify Plant will fall back to the ${_prettify(fallbackTo)} list.` : `Notify Plant will surface a warning for ${meta.label.toLowerCase()} items.`}</span>
        )}
        {(addresses || []).map((a) => (
          <span
            key={a}
            className="inline-flex items-center gap-1 rounded-full bg-white border border-neutral-200 px-2 py-0.5 text-[11px] font-mono"
            data-testid={`plant-list-chip-${meta.key}-${a}`}
          >
            {a}
            <button
              type="button"
              onClick={() => remove(a)}
              className="text-neutral-400 hover:text-red-700"
              aria-label={`Remove ${a}`}
              data-testid={`plant-list-remove-${meta.key}-${a}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-1.5">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
              e.preventDefault();
              addFromDraft();
            }
          }}
          placeholder="plant-team@example.com"
          className="h-8 text-xs"
          data-testid={`plant-list-input-${meta.key}`}
        />
        <Button
          type="button"
          onClick={addFromDraft}
          disabled={!draft.trim()}
          className="h-8 bg-white text-[#0F172A] border border-neutral-300 hover:bg-neutral-50"
          data-testid={`plant-list-add-${meta.key}`}
        >
          <Plus className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
};

// =====================================================================
// Feb 2026 — Near-Expiry Notification Configuration Card
// =====================================================================
// Super-admin only. Per (category × threshold) recipient list. The
// stored config lives on `settings.near_expiry_config` and is read /
// written via `/api/admin/near-expiry/*`. This card is self-contained
// (does NOT reuse the parent AdminSettings save flow) so recipients
// don't leak when a sub-admin saves other Notification fields.

const NEAR_EXPIRY_THRESHOLDS = [0, 90, 120, 180];
const NEAR_EXPIRY_CATEGORIES = [
  { key: 'ice-cream',   label: 'Ice-cream',        brand: 'Glace',           accent: 'text-pink-700',    chip: 'bg-pink-50 border-pink-200' },
  { key: 'chips',       label: 'Chips',            brand: 'Muscat Chips',    accent: 'text-amber-800',   chip: 'bg-amber-50 border-amber-200' },
  { key: 'wafers',      label: 'Wafers',           brand: 'Kingsy Wafers',   accent: 'text-orange-700',  chip: 'bg-orange-50 border-orange-200' },
  { key: 'water',       label: 'Water',            brand: 'Saroj Water',     accent: 'text-sky-700',     chip: 'bg-sky-50 border-sky-200' },
  { key: 'frozen',      label: 'Frozen',           brand: 'Frozen items',    accent: 'text-cyan-700',    chip: 'bg-cyan-50 border-cyan-200' },
  { key: 'mc-ex',       label: 'MC Ex',            brand: 'Muscat Chips \u00b7 Ex',   accent: 'text-yellow-800', chip: 'bg-yellow-50 border-yellow-200' },
  { key: 'chips-rm',    label: 'Chips RM',         brand: 'Chips raw material',       accent: 'text-lime-800',   chip: 'bg-lime-50 border-lime-200' },
  { key: 'glace-rm-pm', label: 'Glace RM / PM',    brand: 'Glace raw & packaging',    accent: 'text-fuchsia-700', chip: 'bg-fuchsia-50 border-fuchsia-200' },
];
const NEAR_EXPIRY_THRESHOLD_META = {
  0:   { label: 'Expired',  hint: 'Already at or past expiry', accent: 'text-red-900',    chip: 'bg-red-100 border-red-300' },
  90:  { label: 'Critical', hint: '\u2264 90 days remaining',  accent: 'text-red-700',    chip: 'bg-red-50 border-red-200' },
  120: { label: 'High',     hint: '\u2264 120 days remaining', accent: 'text-orange-700', chip: 'bg-orange-50 border-orange-200' },
  180: { label: 'Watch',    hint: '\u2264 180 days remaining', accent: 'text-yellow-700', chip: 'bg-yellow-50 border-yellow-200' },
};

const NEAR_EXPIRY_FREQUENCY_OPTIONS = [
  { key: 'daily',    label: 'Daily',           hint: 'Fires every morning at 08:00 Oman' },
  { key: 'weekly',   label: 'Weekly',          hint: 'Once every 7 days' },
  { key: 'biweekly', label: 'Every 15 days',   hint: 'Once every 15 days' },
];

const NearExpiryConfigCard = ({ authHeaders, apiBase }) => {
  const { toast } = useToast();
  const [isSuper, setIsSuper] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [config, setConfig] = useState({});
  const [frequency, setFrequency] = useState('daily');
  const [drafts, setDrafts] = useState({}); // {`${cat}|${t}`: 'new@ex.com'}

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const me = await axios.get(`${apiBase}/admin/me`, { headers: authHeaders });
        if (cancel) return;
        const superOk = !!me.data?.is_super_admin;
        setIsSuper(superOk);
        if (!superOk) { setLoading(false); return; }
        const { data } = await axios.get(`${apiBase}/admin/near-expiry/config`, { headers: authHeaders });
        if (cancel) return;
        setConfig(data?.config || {});
        setFrequency(data?.frequency || 'daily');
      } catch (e) {
        if (!cancel) {
          setIsSuper(false);
          toast({ title: 'Failed to load Near-Expiry config', description: e.response?.data?.detail || e.message });
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [apiBase, authHeaders, toast]);

  const draftKey = (cat, t) => `${cat}|${t}`;
  const listFor = (cat, t) => {
    const c = config[cat] || {};
    const arr = c[String(t)];
    return Array.isArray(arr) ? arr : [];
  };
  const setListFor = (cat, t, next) => {
    const clean = [];
    const seen = new Set();
    (next || []).forEach((raw) => {
      const s = String(raw || '').trim();
      if (!s || seen.has(s.toLowerCase())) return;
      seen.add(s.toLowerCase());
      clean.push(s);
    });
    setConfig((prev) => ({
      ...prev,
      [cat]: { ...(prev[cat] || {}), [String(t)]: clean },
    }));
  };

  const addFromDraft = (cat, t) => {
    const raw = (drafts[draftKey(cat, t)] || '').trim();
    if (!raw) return;
    const parts = raw.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    const invalid = parts.filter((s) => !_isEmail(s));
    const good = parts.filter(_isEmail);
    if (invalid.length) {
      toast({
        title: `Skipped ${invalid.length} invalid address${invalid.length === 1 ? '' : 'es'}`,
        description: invalid.slice(0, 3).join(', ') + (invalid.length > 3 ? '…' : ''),
      });
    }
    if (good.length) setListFor(cat, t, [...listFor(cat, t), ...good]);
    setDrafts((prev) => ({ ...prev, [draftKey(cat, t)]: '' }));
  };
  const removeAddr = (cat, t, addr) => {
    setListFor(cat, t, listFor(cat, t).filter((a) => a !== addr));
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${apiBase}/admin/near-expiry/config`,
        { config, frequency },
        { headers: authHeaders },
      );
      setConfig(data?.config || {});
      setFrequency(data?.frequency || 'daily');
      toast({ title: 'Near-Expiry settings saved' });
    } catch (e) {
      toast({ title: 'Save failed', description: e.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  const runNow = async (dryRun) => {
    setRunning(true);
    try {
      const { data } = await axios.post(
        `${apiBase}/admin/near-expiry/run-now?dry_run=${dryRun ? 'true' : 'false'}`,
        {},
        { headers: authHeaders },
      );
      const tot = data?.totals || {};
      const groupsWithSends = (data?.groups || []).filter((g) => g.recipients?.length > 0);
      toast({
        title: dryRun ? 'Dry-run complete' : 'Scanner fired',
        description: dryRun
          ? `${tot.items_scanned || 0} item(s) scanned across ${tot.groups || 0} group(s); ${groupsWithSends.length} would email.`
          : `${tot.emails_sent || 0} email(s) sent, ${tot.emails_failed || 0} failed across ${tot.groups || 0} group(s).`,
      });
    } catch (e) {
      toast({ title: 'Run failed', description: e.response?.data?.detail || e.message });
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-neutral-200 p-6 lg:p-8 flex items-center gap-3 text-sm text-neutral-500" data-testid="near-expiry-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading Near-Expiry configuration…
      </div>
    );
  }
  if (!isSuper) {
    return (
      <div className="bg-white rounded-2xl border border-neutral-200 p-6 lg:p-8" data-testid="near-expiry-locked">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-neutral-100 text-neutral-500 flex items-center justify-center">
            <Lock className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-[#0F172A]">Near-Expiry Configuration</h2>
            <p className="text-sm text-slate-500">
              Restricted. Only super admins can view or edit the recipient lists for expiry alerts.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-neutral-200 p-6 lg:p-8" data-testid="near-expiry-config">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-50 text-red-700 flex items-center justify-center">
            <Clock className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-[#0F172A]">Near-Expiry Configuration</h2>
            <p className="text-sm text-slate-500">
              Automated scan of warehouse stock at 08:00 Oman on the schedule you choose below.
              Items already <b className="text-red-900">Expired</b>, or expiring in
              <b className="text-red-700"> 90</b>, <b className="text-orange-700">120</b>, or
              <b className="text-yellow-700"> 180</b> days trigger a category-scoped alert email
              (sender name: <span className="font-mono bg-slate-100 px-1 rounded">alert</span>).
              An item hitting multiple windows is reported ONLY at the most urgent one.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Button
            type="button"
            onClick={() => runNow(true)}
            disabled={running}
            className="h-8 text-xs bg-white text-[#0F172A] border border-neutral-300 hover:bg-neutral-50"
            data-testid="near-expiry-dry-run-btn"
          >
            {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3 mr-1" />}
            Dry-run
          </Button>
          <Button
            type="button"
            onClick={() => runNow(false)}
            disabled={running}
            className="h-8 text-xs bg-red-600 hover:bg-red-700 text-white"
            data-testid="near-expiry-run-btn"
          >
            {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
            Run now
          </Button>
        </div>
      </div>

      {/* Feb 2026 — Notification Frequency selector. Gates the
          scheduled cron path only; manual Run-now always fires. */}
      <div className="mb-4 rounded-xl border border-neutral-200 bg-slate-50 p-3" data-testid="near-expiry-frequency-row">
        <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-1.5 flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5" /> Notification Frequency
        </div>
        <div className="flex flex-wrap gap-1.5">
          {NEAR_EXPIRY_FREQUENCY_OPTIONS.map((opt) => {
            const active = frequency === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => setFrequency(opt.key)}
                title={opt.hint}
                className={`h-8 px-3 text-xs font-semibold rounded-lg transition-colors border ${
                  active
                    ? 'bg-[#0078D4] text-white border-[#0078D4]'
                    : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                }`}
                data-testid={`near-expiry-frequency-${opt.key}`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        <div className="text-[11px] text-neutral-500 mt-1.5">
          {NEAR_EXPIRY_FREQUENCY_OPTIONS.find((o) => o.key === frequency)?.hint || 'Choose how often the automated scan runs.'} · Manual <b>Run now</b> always fires immediately.
        </div>
      </div>

      <div className="space-y-4" data-testid="near-expiry-grid">
        {NEAR_EXPIRY_CATEGORIES.map((cat) => (
          <div key={cat.key} className={`rounded-xl border ${cat.chip} p-3`} data-testid={`near-expiry-cat-${cat.key}`}>
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className={`text-sm font-bold ${cat.accent}`}>{cat.label}</div>
                <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold">
                  {cat.brand} <span className="font-mono text-neutral-400 lowercase">· {cat.key}</span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2">
              {NEAR_EXPIRY_THRESHOLDS.map((t) => {
                const meta = NEAR_EXPIRY_THRESHOLD_META[t];
                const list = listFor(cat.key, t);
                const dk = draftKey(cat.key, t);
                return (
                  <div
                    key={t}
                    className={`rounded-lg border ${meta.chip} p-2.5`}
                    data-testid={`near-expiry-slot-${cat.key}-${t}`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <div className={`text-xs font-bold ${meta.accent}`}>
                        {t === 0
                          ? <>Expired <span className="text-[10px] font-normal text-neutral-500">· at/past expiry</span></>
                          : <>{t} d <span className="text-[10px] font-normal text-neutral-500">· {meta.label}</span></>
                        }
                      </div>
                      <span className="text-[10px] text-neutral-600 bg-white/80 px-1.5 py-0.5 rounded-full">
                        {list.length}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1 mb-1.5 min-h-[22px]">
                      {list.length === 0 && (
                        <span className="text-[10px] text-neutral-400 italic">No recipients — this threshold is silent.</span>
                      )}
                      {list.map((a) => (
                        <span
                          key={a}
                          className="inline-flex items-center gap-1 rounded-full bg-white border border-neutral-200 px-1.5 py-0.5 text-[10px] font-mono"
                          data-testid={`near-expiry-chip-${cat.key}-${t}-${a}`}
                        >
                          {a}
                          <button
                            type="button"
                            onClick={() => removeAddr(cat.key, t, a)}
                            className="text-neutral-400 hover:text-red-700"
                            aria-label={`Remove ${a}`}
                            data-testid={`near-expiry-remove-${cat.key}-${t}-${a}`}
                          >
                            <X className="w-2.5 h-2.5" />
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-1">
                      <Input
                        value={drafts[dk] || ''}
                        onChange={(e) => setDrafts((prev) => ({ ...prev, [dk]: e.target.value }))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
                            e.preventDefault();
                            addFromDraft(cat.key, t);
                          }
                        }}
                        placeholder="alert@example.com"
                        className="h-7 text-[11px]"
                        data-testid={`near-expiry-input-${cat.key}-${t}`}
                      />
                      <Button
                        type="button"
                        onClick={() => addFromDraft(cat.key, t)}
                        disabled={!(drafts[dk] || '').trim()}
                        className="h-7 bg-white text-[#0F172A] border border-neutral-300 hover:bg-neutral-50 px-2"
                        data-testid={`near-expiry-add-${cat.key}-${t}`}
                      >
                        <Plus className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 flex items-center justify-between gap-3">
        <div className="text-[11px] text-neutral-500">
          Save persists the frequency AND the recipient lists. Multiple addresses can be pasted comma-separated.
        </div>
        <Button
          type="button"
          onClick={save}
          disabled={saving}
          className="h-9 bg-[#0078D4] hover:bg-[#106EBE] text-white text-sm px-4"
          data-testid="near-expiry-save-btn"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
          Save settings
        </Button>
      </div>
    </div>
  );
};

/**
 * Notifications panel (Admin → Settings → Notifications tab).
 *
 * Controls D7 Networks SMS:
 *   - master `sms_enabled` kill switch for ALL outbound SMS
 *   - back-in-stock customer SMS / email toggles
 *   - admin phone + per-event admin SMS toggles
 *   - "Send test SMS" button hitting POST /api/admin/sms-test
 *
 * Reads/writes `form.notifications` — saved through the parent AdminSettings
 * PUT /api/admin/settings.
 */
const ADMIN_EVENTS = [
  { key: 'new_order',                label: 'New customer order',           hint: 'SMS the admin whenever a checkout completes' },
  { key: 'new_inquiry',              label: 'New B2B trade inquiry',         hint: 'SMS the admin whenever the homepage form is submitted' },
  { key: 'driver_signup',            label: 'New driver signup',             hint: 'SMS the admin when a driver applies and needs approval' },
  { key: 'back_in_stock_subscribed', label: 'Customer signs up for restock', hint: 'SMS the admin when someone subscribes to a "notify me" alert' },
];

const Row = ({ label, hint, children, testId }) => (
  <div className="flex items-start justify-between gap-4 py-3 border-b border-slate-100 last:border-b-0" data-testid={testId}>
    <div className="min-w-0">
      <div className="text-sm font-semibold text-[#0F172A]">{label}</div>
      {hint && <div className="text-xs text-slate-500 mt-0.5">{hint}</div>}
    </div>
    <div className="shrink-0">{children}</div>
  </div>
);

export const NotificationsPanel = ({ form, update, authHeaders, apiBase }) => {
  const { toast } = useToast();
  const notif = form.notifications || {};
  const adminSms = notif.admin_sms || {};
  const [testTo, setTestTo] = useState(notif.admin_phone || '');
  const [testChannel, setTestChannel] = useState('sms');
  const [testing, setTesting] = useState(false);

  const setNotif = (patch) => update({ notifications: { ...notif, ...patch } });
  const setAdminSms = (key, val) => setNotif({ admin_sms: { ...adminSms, [key]: val } });

  const masterOn = !!notif.sms_enabled;

  const sendTest = async () => {
    const to = (testTo || '').trim();
    if (!to) {
      toast({ title: 'Enter a phone number first' });
      return;
    }
    setTesting(true);
    try {
      const { data } = await axios.post(
        `${apiBase}/admin/sms-test`,
        { to, message: 'Mohammed Riaz · D7 integration test. If you received this, the channel is live.', channel: testChannel },
        { headers: authHeaders }
      );
      if (data?.ok) {
        const used = data.channel_used || data.channel || testChannel;
        toast({ title: `Test sent via ${used}`, description: `request_id: ${data.request_id || '(none)'} · status: ${data.status || 'queued'}${data.fallback_from ? ' · fellback from ' + data.fallback_from : ''}` });
      } else {
        toast({ title: 'Test failed', description: data?.error || 'Unknown error' });
      }
    } catch (e) {
      toast({ title: 'Test failed', description: e.response?.data?.detail || e.message });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="notifications-panel">
      {/* Master SMS toggle */}
      <div className="bg-white rounded-2xl border border-neutral-200 p-6 lg:p-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
            <MessageSquare className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-[#0F172A]">SMS via D7 Networks</h2>
            <p className="text-sm text-slate-500">Master kill switch — when OFF, no outbound SMS is sent (customer or admin), regardless of any per-event toggle below.</p>
          </div>
        </div>
        <Row
          label="Enable D7 SMS integration"
          hint={masterOn ? 'SMS sending is ACTIVE. Costs apply per message.' : 'All SMS sending is paused.'}
          testId="row-sms-enabled"
        >
          <Switch
            checked={masterOn}
            onCheckedChange={(v) => setNotif({ sms_enabled: v })}
            data-testid="toggle-sms-enabled"
          />
        </Row>
        {!masterOn && (
          <div className="mt-3 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs flex items-start gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>SMS sending is OFF. Email notifications still work as before.</span>
          </div>
        )}

        {/* Default channel preference — used by every outbound message unless
            a more specific per-event override is set. */}
        {masterOn && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="text-xs font-semibold text-neutral-700 uppercase tracking-wide mb-1.5">
              Default delivery channel
            </div>
            <p className="text-[11px] text-slate-500 mb-2">
              Used for OTP login, back-in-stock alerts, admin events &amp; broadcasts (unless overridden per-event).
            </p>
            <div className="flex flex-wrap gap-1.5" data-testid="default-channel-selector">
              {[
                { code: 'sms',                 label: 'SMS only',          hint: 'Reliable, no opt-in needed' },
                { code: 'whatsapp',            label: 'WhatsApp only',     hint: 'Cheaper, richer, opt-in required' },
                { code: 'whatsapp_then_sms',   label: 'WhatsApp \u2192 SMS', hint: 'Best of both: try WA, fall back to SMS automatically' },
                { code: 'sms_then_whatsapp',   label: 'SMS \u2192 WhatsApp', hint: 'Only useful for niche flows' },
              ].map((opt) => {
                const active = (notif.default_channel || 'sms') === opt.code;
                return (
                  <button
                    key={opt.code}
                    type="button"
                    onClick={() => setNotif({ default_channel: opt.code })}
                    title={opt.hint}
                    className={`h-9 px-3 text-xs font-semibold rounded-lg transition-colors border ${
                      active
                        ? 'bg-[#0078D4] text-white border-[#0078D4]'
                        : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                    }`}
                    data-testid={`btn-default-channel-${opt.code}`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Customer SMS (back in stock) */}
      <div className="bg-white rounded-2xl border border-neutral-200 p-6 lg:p-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-[#EFF6FC] text-[#0078D4] flex items-center justify-center">
            <Bell className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-[#0F172A]">Customer &ldquo;Notify me when back&rdquo; alerts</h2>
            <p className="text-sm text-slate-500">When admin flips a product from out-of-stock to in-stock, queued subscribers are notified by:</p>
          </div>
        </div>
        <Row label="Email" hint="Always works — uses Brevo SMTP.">
          <Switch
            checked={notif.back_in_stock_email !== false}
            onCheckedChange={(v) => setNotif({ back_in_stock_email: v })}
            data-testid="toggle-back-in-stock-email"
          />
        </Row>
        <Row
          label="SMS"
          hint={masterOn ? 'Sent via D7 when the subscriber provided a phone number.' : 'Master SMS toggle is off — enable above first.'}
        >
          <Switch
            checked={!!notif.back_in_stock_sms && masterOn}
            disabled={!masterOn}
            onCheckedChange={(v) => setNotif({ back_in_stock_sms: v })}
            data-testid="toggle-back-in-stock-sms"
          />
        </Row>
      </div>

      {/* Aug 2026 — Category-keyed plant mailing lists. Notify Plant
          picks the list matching the item's category_slug and sends
          to every address. Super-admin only for read + write. The
          settings API strips these on GET for non-supers, and the
          PUT endpoint preserves stored values when a non-super
          submits — so the tag-input controls below only render for
          super admins. */}
      <PlantMailingListsCard form={form} update={update} authHeaders={authHeaders} apiBase={apiBase} />

      {/* Feb 2026 — Near-Expiry Notification Configuration. Runs the
          daily 08:00-Oman cron under `/api/admin/near-expiry/*` and
          persists recipients to `settings.near_expiry_config`. This
          card is self-contained (its own save button) so it doesn't
          share the parent AdminSettings save flow. */}
      <NearExpiryConfigCard authHeaders={authHeaders} apiBase={apiBase} />

      {/* Admin SMS */}
      <div className="bg-white rounded-2xl border border-neutral-200 p-6 lg:p-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-[#0F172A]">Auto-SMS to admin</h2>
            <p className="text-sm text-slate-500">Receive a text on the admin phone whenever any of the events below fires. Independent of email/in-app alerts.</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          <div>
            <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Admin phone</label>
            <Input
              value={notif.admin_phone || ''}
              onChange={(e) => setNotif({ admin_phone: e.target.value })}
              placeholder="+968 9XXX XXXX"
              className="mt-1"
              data-testid="input-admin-phone"
            />
            <p className="text-[11px] text-slate-500 mt-1">Include the country code (e.g. +968). Empty = no admin SMS.</p>
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Admin SMS language</label>
            <div className="flex items-center gap-1.5 mt-1 h-10">
              {[
                { code: 'en', label: 'English' },
                { code: 'ar', label: 'العربية' },
              ].map((opt) => {
                const active = (notif.admin_language || 'en') === opt.code;
                return (
                  <button
                    key={opt.code}
                    type="button"
                    onClick={() => setNotif({ admin_language: opt.code })}
                    className={`h-10 px-4 text-sm font-semibold rounded-md transition-colors border ${
                      active
                        ? 'bg-[#0078D4] text-white border-[#0078D4]'
                        : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                    }`}
                    data-testid={`btn-admin-lang-${opt.code}`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-500 mt-1">Language used for all admin alert SMS (new order / inquiry / driver signup).</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-1 gap-3 mt-3">
          <div>
            <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Test channel</label>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {[
                { code: 'sms',                 label: 'SMS' },
                { code: 'whatsapp',            label: 'WhatsApp' },
                { code: 'whatsapp_then_sms',   label: 'WA \u2192 SMS fallback' },
              ].map((opt) => {
                const active = testChannel === opt.code;
                return (
                  <button
                    key={opt.code}
                    type="button"
                    onClick={() => setTestChannel(opt.code)}
                    className={`h-8 px-3 text-[11px] font-semibold rounded-md transition-colors border ${
                      active
                        ? 'bg-[#0F172A] text-white border-[#0F172A]'
                        : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                    }`}
                    data-testid={`btn-test-channel-${opt.code}`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Send test to</label>
              <Input
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                placeholder="+968 9XXX XXXX"
                className="mt-1"
                data-testid="input-test-sms-to"
              />
            </div>
            <Button
              type="button"
              onClick={sendTest}
              disabled={testing || !masterOn}
              className="bg-[#0078D4] hover:bg-[#106EBE] text-white h-10"
              data-testid="btn-send-test-sms"
            >
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              <span className="ml-1.5">Test</span>
            </Button>
          </div>
        </div>

        <div className="mt-4 border-t border-slate-100 pt-2">
          {ADMIN_EVENTS.map(({ key, label, hint }) => (
            <Row key={key} label={label} hint={hint} testId={`row-admin-sms-${key}`}>
              <Switch
                checked={!!adminSms[key] && masterOn}
                disabled={!masterOn}
                onCheckedChange={(v) => setAdminSms(key, v)}
                data-testid={`toggle-admin-sms-${key}`}
              />
            </Row>
          ))}
        </div>
      </div>

      {/* SMS OTP login */}
      <div className="bg-white rounded-2xl border border-neutral-200 p-6 lg:p-8" data-testid="sms-otp-card">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center">
            <KeyRound className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-[#0F172A]">4-digit SMS OTP login</h2>
            <p className="text-sm text-slate-500">
              Allow users to sign in with a one-time code texted to their phone instead of (or alongside) email-based login. Email + phone must both match the account on file — there&apos;s no account creation via SMS.
            </p>
          </div>
        </div>
        <Row
          label="Customer storefront login"
          hint={masterOn ? 'Adds an "SMS code" tab on the customer login page.' : 'Master SMS toggle is off — enable above first.'}
          testId="row-sms-otp-customer"
        >
          <Switch
            checked={!!notif.auth_sms_otp_customer_enabled && masterOn}
            disabled={!masterOn}
            onCheckedChange={(v) => setNotif({ auth_sms_otp_customer_enabled: v })}
            data-testid="toggle-sms-otp-customer"
          />
        </Row>
        <Row
          label="Admin login"
          hint={masterOn ? 'Adds an "SMS code" tab on /admin/login (admin must have a phone saved).' : 'Master SMS toggle is off — enable above first.'}
          testId="row-sms-otp-admin"
        >
          <Switch
            checked={!!notif.auth_sms_otp_admin_enabled && masterOn}
            disabled={!masterOn}
            onCheckedChange={(v) => setNotif({ auth_sms_otp_admin_enabled: v })}
            data-testid="toggle-sms-otp-admin"
          />
        </Row>
        <Row
          label="Driver login"
          hint={masterOn ? 'Adds an "SMS code" tab on /driver/login.' : 'Master SMS toggle is off — enable above first.'}
          testId="row-sms-otp-driver"
        >
          <Switch
            checked={!!notif.auth_sms_otp_driver_enabled && masterOn}
            disabled={!masterOn}
            onCheckedChange={(v) => setNotif({ auth_sms_otp_driver_enabled: v })}
            data-testid="toggle-sms-otp-driver"
          />
        </Row>
        <div className="mt-3 p-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-700 text-xs flex items-start gap-2">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-slate-500" />
          <span>
            <strong>Pre-flight:</strong> every account that wants to log in this way must have a <code className="px-1 py-0.5 bg-white border border-slate-200 rounded">phone</code> saved on file. Customers — set during checkout/profile. Admins — Profile → phone. Drivers — driver signup form.
          </span>
        </div>
      </div>

      {/* Customer & driver task SMS toggles */}
      <PartyEventsCard notif={notif} setNotif={setNotif} />

      {/* WhatsApp templates */}
      <WhatsAppTemplatesCard notif={notif} setNotif={setNotif} />

      {/* New-customer phone verification (gate) */}
      <div className="bg-white rounded-2xl border border-neutral-200 p-6 lg:p-8" data-testid="phone-verify-card">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-[#0F172A]">Verify new customer mobile</h2>
            <p className="text-sm text-slate-500">
              Sends a 4-digit SMS code at checkout. New customers must enter it before their order is accepted. Existing customers who already verified are skipped automatically.
            </p>
          </div>
        </div>
        <Row
          label="Require phone OTP verification at checkout"
          hint={masterOn ? 'When ON, new customers can\u2019t complete an order until they verify their phone via SMS code.' : 'Master SMS toggle is off — enable above first.'}
          testId="row-require-phone-verify"
        >
          <Switch
            checked={!!notif.require_phone_verify_at_checkout && masterOn}
            disabled={!masterOn}
            onCheckedChange={(v) => setNotif({ require_phone_verify_at_checkout: v })}
            data-testid="toggle-require-phone-verify"
          />
        </Row>
      </div>

      {/* SMS templates editor */}
      <SmsTemplatesEditor
        notif={notif}
        setNotif={setNotif}
        authHeaders={authHeaders}
        apiBase={apiBase}
      />
    </div>
  );
};

/**
 * SMS template editor — fetches the catalog once on mount and renders an
 * editable textarea + placeholder chips + live preview per event. Empty
 * template falls back to the server-side default, so admins can clear an
 * override safely. Preview hits POST /api/admin/sms-templates/preview
 * which renders with sample values — never sends a real SMS.
 */
const TEMPLATE_LANGS = [
  { code: 'en', label: 'English',  dir: 'ltr' },
  { code: 'ar', label: 'العربية', dir: 'rtl' },
];

const TEMPLATE_FIELD_BY_LANG = {
  en: { override: 'sms_templates',    defaultProp: 'default'    },
  ar: { override: 'sms_templates_ar', defaultProp: 'default_ar' },
};

const SmsTemplatesEditor = ({ notif, setNotif, authHeaders, apiBase }) => {
  const { toast } = useToast();
  const [catalog, setCatalog] = useState([]);
  const [previews, setPreviews] = useState({}); // {`${event_key}_${lang}`: {rendered, length}}
  const [loadingPreview, setLoadingPreview] = useState(null);
  // Per-template active lang tab — each event can be edited independently.
  const [activeLang, setActiveLang] = useState({}); // {event_key: 'en' | 'ar'}

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get(`${apiBase}/admin/sms-templates/catalog`, { headers: authHeaders });
        if (mounted) setCatalog(data.templates || []);
      } catch (e) {
        if (mounted) toast({ title: 'Could not load template catalog', description: e.message });
      }
    })();
    return () => { mounted = false; };
  }, [apiBase, authHeaders, toast]);

  const getLang = (entryKey) => activeLang[entryKey] || 'en';
  const getOverride = (lang, entryKey) => (notif[TEMPLATE_FIELD_BY_LANG[lang].override] || {})[entryKey] ?? '';
  const getDefault  = (lang, entry)    => entry[TEMPLATE_FIELD_BY_LANG[lang].defaultProp] || entry.default || '';

  const setTemplate = (lang, key, value) => {
    const field = TEMPLATE_FIELD_BY_LANG[lang].override;
    const next = { ...(notif[field] || {}) };
    if (!value || !value.trim()) {
      delete next[key];
    } else {
      next[key] = value;
    }
    setNotif({ [field]: next });
  };

  const renderPreview = async (entry) => {
    const lang = getLang(entry.key);
    const tplKey = `${entry.key}_${lang}`;
    setLoadingPreview(tplKey);
    try {
      const tpl = getOverride(lang, entry.key) || getDefault(lang, entry);
      const { data } = await axios.post(
        `${apiBase}/admin/sms-templates/preview`,
        { event_key: entry.key, template: tpl, lang },
        { headers: authHeaders }
      );
      if (data?.ok) {
        setPreviews((p) => ({ ...p, [tplKey]: { rendered: data.rendered, length: data.length, lang } }));
      } else {
        toast({ title: 'Preview failed', description: data?.error || 'Unknown error' });
      }
    } catch (e) {
      toast({ title: 'Preview failed', description: e.response?.data?.detail || e.message });
    } finally {
      setLoadingPreview(null);
    }
  };

  const insertPlaceholder = (entry, ph) => {
    const lang = getLang(entry.key);
    const cur = getOverride(lang, entry.key) || getDefault(lang, entry);
    setTemplate(lang, entry.key, cur + `{${ph}}`);
  };

  return (
    <div className="bg-white rounded-2xl border border-neutral-200 p-6 lg:p-8" data-testid="sms-templates-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
          <FileText className="w-5 h-5" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-[#0F172A]">SMS message templates</h2>
          <p className="text-sm text-slate-500">
            Customise the wording per language. Use <code className="px-1.5 py-0.5 bg-slate-100 rounded text-xs">{'{placeholder}'}</code> tokens — they&apos;re replaced at send time. Leave blank to use the language default. Customer language is captured on the &ldquo;Notify me&rdquo; form; admin language is set above.
          </p>
        </div>
      </div>

      <div className="space-y-5">
        {catalog.map((entry) => {
          const lang = getLang(entry.key);
          const langMeta = TEMPLATE_LANGS.find((l) => l.code === lang) || TEMPLATE_LANGS[0];
          const current = getOverride(lang, entry.key);
          const langDefault = getDefault(lang, entry);
          const usingDefault = !current.trim();
          const preview = previews[`${entry.key}_${lang}`];
          const previewLoading = loadingPreview === `${entry.key}_${lang}`;
          // Visual indicator: which languages have an override saved?
          const hasOverride = TEMPLATE_LANGS.reduce((acc, l) => {
            acc[l.code] = !!((notif[TEMPLATE_FIELD_BY_LANG[l.code].override] || {})[entry.key] || '').trim();
            return acc;
          }, {});
          return (
            <div key={entry.key} className="border border-slate-200 rounded-xl p-4" data-testid={`sms-template-row-${entry.key}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold text-[#0F172A]">{entry.label}</span>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${entry.audience === 'customer' ? 'bg-[#EFF6FC] text-[#0E4A7B]' : 'bg-violet-50 text-violet-700'}`}>
                      {entry.audience}
                    </span>
                    {usingDefault && (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                        Using default
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5 font-mono break-all">
                    event: <code>{entry.key}</code>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {!usingDefault && (
                    <button
                      type="button"
                      onClick={() => setTemplate(lang, entry.key, '')}
                      className="h-8 px-2 rounded-md border border-slate-200 hover:bg-slate-50 text-xs text-slate-600 inline-flex items-center gap-1"
                      title={`Reset ${langMeta.label} to default`}
                      data-testid={`btn-sms-reset-${entry.key}-${lang}`}
                    >
                      <RotateCcw className="w-3 h-3" /> Reset
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => renderPreview(entry)}
                    disabled={previewLoading}
                    className="h-8 px-2 rounded-md bg-[#EFF6FC] hover:bg-[#DEECF9] border border-[#BBDCF7] text-[#0E4A7B] text-xs inline-flex items-center gap-1 disabled:opacity-60"
                    data-testid={`btn-sms-preview-${entry.key}`}
                  >
                    {previewLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />}
                    Preview
                  </button>
                </div>
              </div>

              {/* Language tabs */}
              <div className="flex items-center gap-1 mb-2" data-testid={`sms-lang-tabs-${entry.key}`}>
                {TEMPLATE_LANGS.map((l) => {
                  const isActive = l.code === lang;
                  return (
                    <button
                      key={l.code}
                      type="button"
                      onClick={() => setActiveLang((s) => ({ ...s, [entry.key]: l.code }))}
                      className={`relative h-7 px-3 text-[11px] font-bold uppercase tracking-wider rounded-md transition-colors ${
                        isActive
                          ? 'bg-[#0F172A] text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                      data-testid={`btn-sms-lang-${entry.key}-${l.code}`}
                    >
                      {l.label}
                      {hasOverride[l.code] && (
                        <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-white" title="Custom override saved" />
                      )}
                    </button>
                  );
                })}
              </div>

              <textarea
                value={current}
                placeholder={langDefault}
                dir={langMeta.dir}
                onChange={(e) => setTemplate(lang, entry.key, e.target.value)}
                rows={2}
                className={`w-full text-sm rounded-md border border-slate-300 focus:border-[#0078D4] focus:ring-1 focus:ring-[#0078D4] outline-none p-2.5 leading-relaxed resize-y ${
                  lang === 'ar' ? 'font-sans text-base' : 'font-mono'
                }`}
                style={langMeta.dir === 'rtl' ? { textAlign: 'right' } : undefined}
                data-testid={`textarea-sms-${entry.key}-${lang}`}
              />

              <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                <span className="text-[11px] text-slate-500 mr-1">Insert:</span>
                {entry.placeholders.map((ph) => (
                  <button
                    type="button"
                    key={ph.key}
                    onClick={() => insertPlaceholder(entry, ph.key)}
                    title={ph.hint}
                    className="h-7 px-2 rounded-md bg-slate-100 hover:bg-slate-200 text-[11px] font-mono text-slate-700 transition-colors"
                    data-testid={`btn-sms-insert-${entry.key}-${ph.key}`}
                  >
                    {`{${ph.key}}`}
                  </button>
                ))}
              </div>

              {preview && (
                <div
                  className="mt-3 p-3 rounded-lg bg-emerald-50 border border-emerald-200"
                  data-testid={`sms-preview-${entry.key}`}
                  dir={preview.lang === 'ar' ? 'rtl' : 'ltr'}
                >
                  <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 mb-1" dir="ltr">
                    Preview · {preview.lang.toUpperCase()} · {preview.length} chars {preview.length > 160 && '· will be billed as multipart SMS'}
                  </div>
                  <div className={`text-sm text-emerald-900 whitespace-pre-wrap ${preview.lang === 'ar' ? 'text-right' : ''}`}>
                    {preview.rendered}
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {catalog.length === 0 && (
          <div className="text-sm text-slate-500">Loading templates…</div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPanel;

// ---------------------------------------------------------------------------
// Customer & driver task SMS events
// ---------------------------------------------------------------------------
// One toggle per transactional SMS event so the admin can dial each flow on
// or off independently. The matching `_<event_key>_enabled` flag is what the
// backend `_notify_party_sms()` helper actually gates on.
const CUSTOMER_EVENTS = [
  { key: 'customer_order_placed',            label: 'Order received',         hint: 'Send when a customer places a new order' },
  { key: 'customer_order_assigned',          label: 'Driver assigned',         hint: 'Send when an admin assigns or auto-assigns a driver' },
  { key: 'customer_order_out_for_delivery',  label: 'Out for delivery',        hint: 'Send when the order goes out for delivery' },
  { key: 'customer_order_delivered',         label: 'Delivered',               hint: 'Send when the driver marks the order as delivered' },
];

const DRIVER_EVENTS = [
  { key: 'driver_new_assignment',  label: 'New order assigned to driver', hint: 'Send to the driver the moment a new order is assigned to them' },
  { key: 'driver_route_updated',   label: 'Route updated',                 hint: 'Send when the driver\u2019s route changes mid-shift' },
];

const PartyEventsCard = ({ notif, setNotif }) => {
  const masterOn = !!notif.sms_enabled;
  return (
    <div className="bg-white rounded-2xl border border-neutral-200 p-6 lg:p-8" data-testid="party-events-card">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center">
          <Bell className="w-5 h-5" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-[#0F172A]">Customer &amp; driver task SMS</h2>
          <p className="text-sm text-slate-500">
            Transactional alerts that fire automatically on the order lifecycle. Edit the wording from the <em>SMS message templates</em> card below — each event is a separate template.
          </p>
        </div>
      </div>

      <div className="mt-5">
        <div className="text-[10px] font-bold uppercase tracking-widest text-[#0E4A7B] mb-2 flex items-center gap-1.5">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#0078D4]" /> CUSTOMER ALERTS
        </div>
        {CUSTOMER_EVENTS.map(({ key, label, hint }) => (
          <Row key={key} label={label} hint={hint} testId={`row-customer-event-${key}`}>
            <Switch
              checked={!!notif[`${key}_enabled`] && masterOn}
              disabled={!masterOn}
              onCheckedChange={(v) => setNotif({ [`${key}_enabled`]: v })}
              data-testid={`toggle-${key}`}
            />
          </Row>
        ))}
      </div>

      <div className="mt-5">
        <div className="text-[10px] font-bold uppercase tracking-widest text-violet-700 mb-2 flex items-center gap-1.5">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-violet-500" /> DRIVER ALERTS
        </div>
        {DRIVER_EVENTS.map(({ key, label, hint }) => (
          <Row key={key} label={label} hint={hint} testId={`row-driver-event-${key}`}>
            <Switch
              checked={!!notif[`${key}_enabled`] && masterOn}
              disabled={!masterOn}
              onCheckedChange={(v) => setNotif({ [`${key}_enabled`]: v })}
              data-testid={`toggle-${key}`}
            />
          </Row>
        ))}
      </div>

      {!masterOn && (
        <div className="mt-3 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs flex items-start gap-2">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>Toggles are locked because the master <strong>D7 SMS</strong> switch is OFF (top of this tab). Flip it on first.</span>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// WhatsApp Templates configuration card
// ---------------------------------------------------------------------------
// Meta requires APPROVED templates for any proactive WhatsApp message sent
// outside the 24-hour customer-service window (so basically: all OTPs,
// restock alerts, and broadcasts). This card lets the admin enter the
// template name + language code as approved in their WhatsApp Business
// Manager. When blank, the corresponding flow falls back to SMS to keep
// deliverability intact.

const WA_USE_CASES = [
  {
    key: 'otp_login',
    label: 'OTP / login codes',
    audience: 'Authentication',
    hint: 'Single body parameter {{1}} = 4-digit code. Meta auto-approves authentication templates within ~1 hour.',
    params: '{{1}} = code',
  },
  {
    key: 'back_in_stock',
    label: 'Back-in-stock alert',
    audience: 'Utility / Marketing',
    hint: 'Two body parameters: {{1}} = product name, {{2}} = link to the product page.',
    params: '{{1}} = product, {{2}} = link',
  },
  {
    key: 'broadcast',
    label: 'Marketing broadcast',
    audience: 'Marketing',
    hint: 'Two body parameters: {{1}} = customer name, {{2}} = message body. Recipients must have opted in.',
    params: '{{1}} = name, {{2}} = message',
  },
];

const WhatsAppTemplatesCard = ({ notif, setNotif }) => {
  const wa = notif.wa_templates || {};
  const update = (k, patch) => {
    const next = { ...wa, [k]: { ...(wa[k] || {}), ...patch } };
    setNotif({ wa_templates: next });
  };
  const clearOne = (k) => {
    const next = { ...wa };
    delete next[k];
    setNotif({ wa_templates: next });
  };

  return (
    <div className="bg-white rounded-2xl border border-neutral-200 p-6 lg:p-8" data-testid="wa-templates-card">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
          <FileText className="w-5 h-5" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-[#0F172A]">WhatsApp template names</h2>
          <p className="text-sm text-slate-500">
            Required for proactive WhatsApp delivery (OTP, restock, broadcasts). Enter the exact name + language code as <strong>APPROVED</strong> in your Meta Business Manager (WhatsApp Manager → Message templates). If blank, the flow uses SMS only.
          </p>
        </div>
      </div>

      <div className="mt-4 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs flex items-start gap-2">
        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
        <span>
          <strong>Why this matters:</strong> Free-form WhatsApp text is silently dropped by Meta if sent outside the 24-hour customer-service window. Approved templates bypass that restriction. Until you fill in template names, WhatsApp will only deliver to customers who messaged you recently — SMS fallback covers everyone else.
        </span>
      </div>

      <div className="space-y-4 mt-5">
        {WA_USE_CASES.map((u) => {
          const cur = wa[u.key] || {};
          const filled = !!(cur.name || '').trim();
          return (
            <div key={u.key} className="border border-slate-200 rounded-xl p-4" data-testid={`wa-tpl-row-${u.key}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold text-[#0F172A]">{u.label}</span>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                      {u.audience}
                    </span>
                    {!filled && (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                        Not configured · SMS only
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{u.hint}</div>
                </div>
                {filled && (
                  <button
                    type="button"
                    onClick={() => clearOne(u.key)}
                    className="h-8 px-2 rounded-md border border-slate-200 hover:bg-slate-50 text-xs text-slate-600 inline-flex items-center gap-1"
                    title="Clear template config (falls back to SMS)"
                    data-testid={`btn-wa-tpl-clear-${u.key}`}
                  >
                    Clear
                  </button>
                )}
              </div>
              <div className="grid sm:grid-cols-2 gap-2 mt-2">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-700">Template name</label>
                  <Input
                    value={cur.name || ''}
                    onChange={(e) => update(u.key, { name: e.target.value })}
                    placeholder="e.g. otp_login_v1"
                    className="mt-0.5 font-mono text-sm"
                    data-testid={`input-wa-tpl-name-${u.key}`}
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-700">Language code</label>
                  <Input
                    value={cur.lang || ''}
                    onChange={(e) => update(u.key, { lang: e.target.value })}
                    placeholder="en, en_US, ar"
                    className="mt-0.5 font-mono text-sm"
                    data-testid={`input-wa-tpl-lang-${u.key}`}
                  />
                </div>
              </div>
              <div className="mt-2 text-[10px] text-slate-500">
                Body parameters: <code className="px-1 py-0.5 bg-slate-100 rounded font-mono">{u.params}</code>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

